<script lang="ts">
import { PullRequest } from '../models/state';
import CreatePullRequest from './PullRequestCreate.svelte';
import { onMount } from 'svelte';
import { appStore } from '../stores/appStore';
import { AppApiClient } from '../api_contracts/app.client';

	

	// Props
	let { repositoryName, ownerId }: { repositoryName: string; ownerId: string } = $props();

	// State
	let client: AppApiClient | null = $state(null);
	let token: string = $state('');
	let pullRequests: PullRequest[] = $state([]);
	let selectedFilter = $state('open');
	let searchQuery = $state('');
	let viewMode = $state('list'); // 'list' | 'create'
	let loading = $state(true);
	let error = $state('');

	// Counts
	let totalCount = $state(0);
	let openCount = $state(0);
	let closedCount = $state(0);
	let mergedCount = $state(0);

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
		token = state.token;
	});

	async function fetchPullRequests() {
		if (!client || !token || !repositoryName || !ownerId) {
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.getPullRequests({
				token,
				ownerId,
				repositoryName,
				state: selectedFilter === 'all' ? 'all' : selectedFilter,
				page: 1,
				pageSize: 30,
			});

			if (result.response.successFull) {
				// Map the API response to our PullRequest interface
				pullRequests = (result.response.pullRequests || []).map((pr: any) => ({
					id: parseInt(pr.id) || 0,
					title: pr.title || '',
					number: pr.number || 0,
					author: pr.authorName || '',
					createdAt: formatDate(pr.createdAt),
					state: pr.state as 'open' | 'closed' | 'merged',
					labels: (pr.labels || []).map((l: any) => ({
						name: l.name || '',
						color: l.color || '#ccc',
					})),
					comments: pr.commentCount || 0,
					additions: pr.additions || 0,
					deletions: pr.deletions || 0,
					reviewStatus: pr.reviewStatus as 'approved' | 'changes-requested' | 'pending' | null,
				}));

				totalCount = result.response.totalCount || 0;
				openCount = result.response.openCount || 0;
				closedCount = result.response.closedCount || 0;
				mergedCount = result.response.mergedCount || 0;
			} else {
				error = result.response.reason || 'Failed to fetch pull requests';
			}
		} catch (e: any) {
			error = e.message || 'Error fetching pull requests';
			console.error('Error fetching pull requests:', e);
		} finally {
			loading = false;
		}
	}

	function formatDate(timestamp: any): string {
		if (!timestamp) return 'unknown';
		try {
			const date = new Date(timestamp.seconds * 1000);
			const now = new Date();
			const diff = now.getTime() - date.getTime();
			const days = Math.floor(diff / (1000 * 60 * 60 * 24));

			if (days === 0) return 'today';
			if (days === 1) return '1 day ago';
			if (days < 7) return `${days} days ago`;
			if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
			return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
		} catch {
			return 'unknown';
		}
	}

	function handleCreate(event: CustomEvent) {
		console.log('New PR created:', event.detail);
		viewMode = 'list';
		// Refresh the list
		fetchPullRequests();
	}

	// Watch for filter changes
	$effect(() => {
		if (selectedFilter) {
			fetchPullRequests();
		}
	});

	onMount(() => {
		fetchPullRequests();
	});
</script>

{#if viewMode === 'create'}
	<CreatePullRequest
		{repositoryName}
		{ownerId}
		on:cancel={() => viewMode = 'list'}
		on:create={handleCreate}
	/>
{:else if loading}
	<div class="flex items-center justify-center min-h-[400px]">
		<div class="text-center">
			<div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
			<p class="mt-4 text-gray-600 dark:text-gray-400">Loading pull requests...</p>
		</div>
	</div>
{:else if error}
	<div class="p-8 text-center">
		<svg class="w-16 h-16 mx-auto text-red-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
			<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
		</svg>
		<p class="text-red-600 dark:text-red-400 text-lg font-semibold">{error}</p>
	</div>
{:else}
<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
	<!-- Header -->
	<div class="p-4 border-b border-gray-200 dark:border-gray-700">
		<div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
			<div class="flex items-center gap-4">
				<button
					onclick={() => (selectedFilter = 'open')}
					class="flex items-center gap-2 text-sm rounded-full px-2 py-1 {selectedFilter === 'open'
						? 'text-gray-900 dark:text-gray-100 font-semibold'
						: 'text-gray-600 dark:text-gray-400'}"
				>
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
						/>
					</svg>
					{openCount} Open
				</button>
				<button
					onclick={() => (selectedFilter = 'closed')}
					class="flex items-center gap-2 text-sm rounded-full px-2 py-1 {selectedFilter === 'closed'
						? 'text-gray-900 dark:text-gray-100 font-semibold'
						: 'text-gray-600 dark:text-gray-400'}"
				>
					<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
							clip-rule="evenodd"
						/>
					</svg>
					{closedCount} Closed
				</button>
				<button
					onclick={() => (selectedFilter = 'merged')}
					class="flex items-center gap-2 text-sm rounded-full px-2 py-1 {selectedFilter === 'merged'
						? 'text-gray-900 dark:text-gray-100 font-semibold'
						: 'text-gray-600 dark:text-gray-400'}"
				>
					<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z"
							clip-rule="evenodd"
						/>
					</svg>
					{mergedCount} Merged
				</button>
			</div>
			<button
				onclick={() => viewMode = 'create'}
				class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
			>
				New Pull Request
			</button>
		</div>

		<!-- Search and Filters -->
		<div class="mt-4 flex flex-col sm:flex-row gap-2">
			<input
				type="text"
				bind:value={searchQuery}
				placeholder="Search pull requests..."
				class="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
			/>
			<select
				class="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
			>
				<option>Sort: Newest</option>
				<option>Sort: Oldest</option>
				<option>Sort: Most commented</option>
				<option>Sort: Recently updated</option>
			</select>
		</div>
	</div>

	<!-- Pull Requests List -->
	<div class="divide-y divide-gray-200 dark:divide-gray-700">
		{#each pullRequests.filter((pr) => pr.state === selectedFilter) as pr}
			<div class="p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
				<div class="flex items-start gap-3">
					<div class="mt-1">
						{#if pr.state === 'open'}
							<svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
								/>
							</svg>
						{:else if pr.state === 'merged'}
							<svg class="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z"
									clip-rule="evenodd"
								/>
							</svg>
						{:else}
							<svg class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
									clip-rule="evenodd"
								/>
							</svg>
						{/if}
					</div>
					<div class="flex-1 min-w-0">
						<div class="flex items-start gap-2 flex-wrap">
							<h3 class="text-gray-900 dark:text-gray-100 font-medium hover:text-blue-600 cursor-pointer">
								{pr.title}
							</h3>
							{#each pr.labels as label}
								<span
									class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
									style="background-color: {label.color}20; color: {label.color}; border: 1px solid {label.color}40;"
								>
									{label.name}
								</span>
							{/each}
						</div>
						<div class="mt-1 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 flex-wrap">
							<span>#{pr.number} opened {pr.createdAt} by {pr.author}</span>
							{#if pr.reviewStatus}
								<span class="flex items-center gap-1">
									{#if pr.reviewStatus === 'approved'}
										<svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
											<path
												fill-rule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clip-rule="evenodd"
											/>
										</svg>
										<span class="text-green-600">Approved</span>
									{:else if pr.reviewStatus === 'changes-requested'}
										<svg class="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
											<path
												fill-rule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
												clip-rule="evenodd"
											/>
										</svg>
										<span class="text-red-600">Changes requested</span>
									{/if}
								</span>
							{/if}
						</div>
						<div class="mt-2 flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
							<span class="flex items-center gap-1">
								<span class="text-green-600">+{pr.additions}</span>
								<span class="text-red-600">-{pr.deletions}</span>
							</span>
							{#if pr.comments > 0}
								<span class="flex items-center gap-1">
									<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
										<path
											fill-rule="evenodd"
											d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
											clip-rule="evenodd"
										/>
									</svg>
									{pr.comments}
								</span>
							{/if}
						</div>
					</div>
				</div>
			</div>
		{/each}
	</div>

	<!-- Empty State -->
	{#if pullRequests.filter((pr) => pr.state === selectedFilter).length === 0}
		<div class="p-12 text-center">
			<svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
				/>
			</svg>
			<h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
				No {selectedFilter} pull requests
			</h3>
			<p class="text-gray-600 dark:text-gray-400">
				There are no {selectedFilter} pull requests at this time.
			</p>
		</div>
	{/if}
</div>
{/if}